import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import axios from "axios";
import {flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import {Alert, Button, Dropdown, Form, Spinner, Table} from "react-bootstrap";
import {
    PiCaretDown,
    PiCaretRight,
    PiCaretUp,
    PiCaretUpDown,
    PiEye,
    PiFunnel,
    PiFunnelFill,
    PiMagnifyingGlass,
    PiMicrosoftExcelLogo,
} from "react-icons/pi";
import HelpTooltip from "../HelpTooltip.jsx";
import SelectList from "../SelectList.jsx";
import AdvancedSearchModal, {EMPTY_SEARCH, hasCriteria} from "../Stock/AdvancedSearchModal.jsx";

/**
 * Serverseitige Tabellen-Komponente auf Basis von @tanstack/react-table -
 * eigenständig neben der geteilten InertiaTable, gedacht für Seiten mit
 * großen Datenmengen (mehrere Tausend Zeilen), bei denen Filtern/Sortieren
 * im Browser nicht mehr ruckelfrei ist. Suche, Spaltenfilter, Sortierung und
 * Paging laufen deshalb als Anfrage an den Server (manualFiltering,
 * manualSorting, manualPagination) - @tanstack/react-table liefert hier nur
 * noch Spaltendefinitionen, Sichtbarkeit und die Zellendarstellung.
 *
 * Über `column.columnDef.meta` wird die Filterzeile gesteuert:
 *   - meta.filterVariant: "text" | "select" | "number" (fehlt -> kein Filter)
 *   - meta.selectOptions: [{value, label}, ...] für "select"
 * Der jeweilige Filterwert wird 1:1 in einen Server-Parameter übersetzt:
 * "text"/"select" -> filter[<id>], "number" -> filter[<id>][min]/[max].
 *
 * Gruppierungsmodus (optional über `groupingColumnId`): lädt zunächst nur
 * die Gruppen (Name + Anzahl) und holt die Zeilen einer Gruppe erst beim
 * Aufklappen nach - das hält auch bei vielen Gruppen die Anfangslast klein.
 *
 * Zusatzfilter im Filter-Popover (`additionalFilters`, jeweils {key, label,
 * defaultValue, help?, type?, options?}): ohne `type` ein Boolean-Schalter
 * wie bisher (Server-Parameter `<key>=1|0`). `type: "date"` liefert ein
 * einzelnes Datumsfeld, `type: "select"` eine Auswahlliste (`options`
 * erforderlich) - beide senden bei gesetztem Wert `filter[<key>]` (oder
 * `paramKey`, falls abweichend) und werden bei leerem Wert weggelassen. Für
 * Filter, die NICHT in die Spaltenfilterzeile gehören (z. B. ein Zeitraum
 * "von"/"bis").
 */
/** Blockgröße beim Nachladen einer aufgeklappten Gruppe (Server-Obergrenze). */
const GROUP_CHUNK_SIZE = 1000;

const DataGrid = forwardRef(function DataGrid(
    {
        columns,
        getRowId,
        initialColumnFilters = [],
        initialSorting = [],
        initialColumnVisibility = {},
        initialColumnOrder = [],
        lockedColumnIds = [],
        // Optional gegliederte Darstellung im Spaltenmenü. Ohne diese Prop
        // bleibt das bisherige einspaltige Menü für alle anderen Tabellen
        // unverändert.
        columnPickerGroups = [],
        // Der Bearbeitungsmodus wird von der aufrufenden Seite gesteuert. Nur
        // darin sind Tabellenköpfe direkt per Drag & Drop umsortierbar.
        columnReorderMode = false,
        groupingColumnId = null,
        emptyLabel = "Keine Einträge gefunden.",
        dataUrl,
        groupsUrl,
        exportUrl,
        additionalFilters = [],
        // Optional ausschließlich für Tabellen, die komplexe serverseitige
        // Bedingungsgruppen anbieten. Ohne Konfiguration bleibt DataGrid wie
        // bisher und sendet keinen advanced_search-Parameter.
        advancedSearchConfig = null,
        // Seit die Liste beim Scrollen nachlädt, gibt es keine Seitenauswahl
        // mehr. Der Wert bestimmt jetzt, wie viele Zeilen pro Nachschub geholt
        // werden. Das frühere pageSizeOptions wird nicht mehr ausgewertet.
        defaultPageSize = 100,
        // Wie die gezählten Zeilen heißen - die Komponente wird von mehreren
        // Seiten mit anderem Inhalt genutzt. Zwei Formen, weil die Fußzeile
        // den Dativ braucht ("von 66 Einträgen") und die Zählung oben rechts
        // den Nominativ ("66 Einträge").
        itemLabel = "Artikel",
        itemLabelDative = "Artikeln",
        // Erlaubt einer einzelnen Tabelle kompakte, umbrechende Kopfzeilen,
        // ohne die einzeilige Darstellung anderer DataGrid-Seiten zu ändern.
        compactHeader = false,
        toolbarActions = null,
        resetAction = null,
        onStateChange = null,
    },
    ref
) {
    const [sorting, setSorting] = useState(initialSorting);
    const [columnFilters, setColumnFilters] = useState(initialColumnFilters);
    const [columnVisibility, setColumnVisibility] = useState(initialColumnVisibility);
    const normaliseColumnOrder = useCallback((candidate = []) => {
        const knownIds = columns.map((column) => column.id);
        const requested = Array.isArray(candidate) ? candidate.filter((id) => knownIds.includes(id)) : [];
        return [...new Set([...requested, ...knownIds])];
    }, [columns]);
    const [columnOrder, setColumnOrder] = useState(() => normaliseColumnOrder(initialColumnOrder));

    // Preis-Spalten werden erst mit den Inertia-Props aufgebaut. Neue Spalten
    // kommen deshalb ans Ende, ohne eine zuvor gespeicherte Reihenfolge zu
    // verlieren; entfernte Spalten werden still aus alten Ansichten entfernt.
    useEffect(() => {
        setColumnOrder((current) => normaliseColumnOrder(current));
    }, [normaliseColumnOrder]);

    const [searchInput, setSearchInput] = useState("");
    const [searchCommitted, setSearchCommitted] = useState("");
    const [advancedSearch, setAdvancedSearch] = useState(EMPTY_SEARCH);
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    const [additionalFilterValues, setAdditionalFilterValues] = useState(() =>
        Object.fromEntries(additionalFilters.map((f) => [f.key, f.defaultValue]))
    );
    const [grouping, setGrouping] = useState(false);

    // Flacher Modus (Standard): eine Seite Zeilen vom Server.
    const [page, setPage] = useState(1);
    const pageSize = defaultPageSize;
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Gruppierungsmodus: Gruppenliste + je Gruppe zwischengespeicherte Zeilen.
    const [groupsList, setGroupsList] = useState([]);
    const [groupsTotal, setGroupsTotal] = useState(0);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsError, setGroupsError] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [groupRowsCache, setGroupRowsCache] = useState({});
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(null);
    const [scrollMaxHeight, setScrollMaxHeight] = useState(null);

    const columnMetaById = useMemo(
        () => Object.fromEntries(columns.map((c) => [c.id, c.meta || {}])),
        [columns]
    );

    // Sticky-Header: die zweite Kopfzeile (Filter) muss exakt unter der ersten
    // (Spaltenüberschriften) kleben. `offsetHeight` rundet bei Browser-Zoom auf
    // ganze Pixel; dadurch blieb zwischen beiden Zeilen ein subpixelbreiter
    // Streifen frei, in dem beim Scrollen Tabellenzeilen sichtbar wurden.
    // Die gerenderte Höhe plus ResizeObserver folgt dagegen auch Umbrüchen und
    // responsiven Breiten exakt.
    const headerRowRef = useRef(null);
    // Der Bereich, in dem tatsächlich gescrollt wird - Bezugspunkt sowohl für
    // die Höhenmessung als auch für das Nachladen beim Scrollen.
    const scrollContainerRef = useRef(null);
    const [filterRowTop, setFilterRowTop] = useState(0);

    useLayoutEffect(() => {
        const measure = () => {
            if (headerRowRef.current) {
                const height = headerRowRef.current.getBoundingClientRect().height;
                setFilterRowTop((current) => (current === height ? current : height));
            }
        };
        measure();
        const resizeObserver = new ResizeObserver(measure);
        if (headerRowRef.current) {
            resizeObserver.observe(headerRowRef.current);
        }
        window.addEventListener("resize", measure);
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener("resize", measure);
        };
    }, [columnVisibility, grouping]);

    // Die Tabelle soll genau bis zum unteren Bildschirmrand reichen und dort
    // aufhören - gescrollt wird ausschliesslich in ihr, nicht in der Seite.
    // Ein fester Abzug reicht dafür nicht: Über der Tabelle stehen je nach
    // Lage unterschiedlich hohe Elemente (etwa der Hinweis auf einen
    // veralteten Stand). Deshalb wird der tatsächliche Abstand zum oberen
    // Rand gemessen. Ohne Abhängigkeitsliste, damit jede Änderung darüber
    // erfasst wird; der Zustand wird nur bei echter Abweichung gesetzt,
    // sonst würde sich das Messen endlos selbst auslösen.
    useLayoutEffect(() => {
        const messen = () => {
            const el = scrollContainerRef.current;
            if (!el) {
                return;
            }
            const abstandOben = el.getBoundingClientRect().top;
            // Etwas Luft für den unteren Seitenrand, damit nicht wegen weniger
            // Pixel doch noch ein Seiten-Rollbalken auftaucht.
            const platz = Math.max(200, Math.round(window.innerHeight - abstandOben - 24));
            setScrollMaxHeight((bisher) => (bisher === platz ? bisher : platz));
        };

        messen();
        window.addEventListener("resize", messen);

        return () => window.removeEventListener("resize", messen);
    });

    const stickyHeaderStyle = {
        position: "sticky",
        top: 0,
        zIndex: 4,
        // Nicht den transparenten Tabellen-Akzent durchlassen, wenn eine
        // Datenzeile unter den sticky Zellen entlangscrollt.
        backgroundColor: "var(--bs-body-bg, #fff)",
        backgroundClip: "border-box",
        boxShadow: "inset 0 -1px 0 var(--bs-border-color, #dee2e6)",
    };
    const stickyFilterStyle = {
        position: "sticky",
        // Ein kontrollierter 1px-Überlapp unter dem höher gestapelten Header
        // deckt auch gerundete Border-Pixel vollständig ab.
        top: Math.max(0, filterRowTop - 1),
        zIndex: 3,
        backgroundColor: "var(--bs-body-bg, #fff)",
        backgroundClip: "border-box",
        boxShadow: "inset 0 -1px 0 var(--bs-border-color, #dee2e6)",
    };

    // ----- Aufbau der Server-Parameter -------------------------------------

    const buildFilterParams = useCallback(() => {
        const params = {};
        columnFilters.forEach(({id, value}) => {
            if (value === undefined || value === null || value === "") {
                return;
            }
            const variant = columnMetaById[id]?.filterVariant;
            if (variant === "number") {
                if (value.min !== undefined && value.min !== null && value.min !== "") {
                    params[`filter[${id}][min]`] = value.min;
                }
                if (value.max !== undefined && value.max !== null && value.max !== "") {
                    params[`filter[${id}][max]`] = value.max;
                }
            } else {
                params[`filter[${id}]`] = value;
            }
        });
        return params;
    }, [columnFilters, columnMetaById]);

    const buildAdditionalParams = useCallback(() => {
        const params = {};
        additionalFilters.forEach((f) => {
            const val = additionalFilterValues[f.key];
            // "date"/"select" sind eigene Server-Filter (filter[<key>]) und werden
            // nur bei gesetztem Wert mitgeschickt - anders als die ursprünglichen
            // Boolean-Schalter, die immer "1"/"0" senden.
            if (f.type === "date" || f.type === "select") {
                if (val) {
                    params[f.paramKey || `filter[${f.key}]`] = val;
                }
                return;
            }
            params[f.key] = val ? "1" : "0";
        });
        return params;
    }, [additionalFilters, additionalFilterValues]);

    const buildAdvancedSearchParams = useCallback(() => {
        if (!advancedSearchConfig || !hasCriteria(advancedSearch)) {
            return {};
        }

        return {advanced_search: JSON.stringify(advancedSearch)};
    }, [advancedSearch, advancedSearchConfig]);

    // ----- Flacher Modus: eine Seite laden -----------------------------------

    const dataAbortRef = useRef(null);
    const prevFlatSignatureRef = useRef(null);
    const loadMoreRef = useRef(null);

    useEffect(() => {
        if (grouping) {
            return undefined;
        }

        const signature = JSON.stringify({sorting, columnFilters, searchCommitted, additionalFilterValues, advancedSearch});
        const signatureChanged = signature !== prevFlatSignatureRef.current;
        prevFlatSignatureRef.current = signature;
        const requestPage = signatureChanged ? 1 : page;
        if (signatureChanged && page !== 1) {
            setPage(1);
        }

        const controller = new AbortController();
        dataAbortRef.current?.abort();
        dataAbortRef.current = controller;
        setLoading(true);
        setError(null);

        const params = {
            page: requestPage,
            per_page: pageSize,
            ...buildAdditionalParams(),
            ...buildFilterParams(),
            ...buildAdvancedSearchParams(),
        };
        if (searchCommitted) {
            params.search = searchCommitted;
        }
        if (sorting[0]) {
            params.sort = sorting[0].id;
            params.dir = sorting[0].desc ? "desc" : "asc";
        }

        axios
            .get(dataUrl, {params, signal: controller.signal})
            .then(({data}) => {
                const incoming = Array.isArray(data?.rows) ? data.rows : [];
                // Beim ersten Block (oder nach jeder Änderung an Suche, Filter
                // und Sortierung) wird ersetzt, danach angehängt - die Liste
                // wächst beim Scrollen, statt sich seitenweise auszutauschen.
                setRows((prev) => (requestPage === 1 ? incoming : [...prev, ...incoming]));
                setTotal(Number.isFinite(data?.total) ? data.total : 0);
                setLastPage(Number.isFinite(data?.last_page) ? data.last_page : 1);
            })
            .catch((e) => {
                if (axios.isCancel(e)) {
                    return;
                }
                setError(e.response?.data?.message || e.message || "Die Artikel konnten nicht geladen werden.");
            })
            .finally(() => {
                if (dataAbortRef.current === controller) {
                    setLoading(false);
                }
            });

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grouping, page, pageSize, sorting, columnFilters, searchCommitted, additionalFilterValues, advancedSearch, dataUrl]);

    // ----- Gruppierungsmodus: Gruppenliste laden -----------------------------

    const groupsAbortRef = useRef(null);
    const groupRowAbortMapRef = useRef({});

    useEffect(() => {
        if (!grouping) {
            return undefined;
        }

        // Alter Zeilen-Cache passt nicht mehr zu einem neuen Filter-/Suchstand -
        // sonst zeigt eine wiedergeöffnete Gruppe Zeilen vom vorherigen Stand.
        Object.values(groupRowAbortMapRef.current).forEach((controller) => controller.abort());
        groupRowAbortMapRef.current = {};
        setExpandedGroups({});
        setGroupRowsCache({});

        const controller = new AbortController();
        groupsAbortRef.current?.abort();
        groupsAbortRef.current = controller;
        setGroupsLoading(true);
        setGroupsError(null);

        const params = {
            ...buildAdditionalParams(),
            ...buildFilterParams(),
            ...buildAdvancedSearchParams(),
        };
        if (searchCommitted) {
            params.search = searchCommitted;
        }

        axios
            .get(groupsUrl, {params, signal: controller.signal})
            .then(({data}) => {
                setGroupsList(Array.isArray(data?.groups) ? data.groups : []);
                setGroupsTotal(Number.isFinite(data?.total) ? data.total : 0);
            })
            .catch((e) => {
                if (axios.isCancel(e)) {
                    return;
                }
                setGroupsError(e.response?.data?.message || e.message || "Die Warengruppen konnten nicht geladen werden.");
            })
            .finally(() => {
                if (groupsAbortRef.current === controller) {
                    setGroupsLoading(false);
                }
            });

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [grouping, sorting, columnFilters, searchCommitted, additionalFilterValues, advancedSearch, groupsUrl]);

    // Eine Warengruppe wird blockweise nachgeladen. `page` > 1 hängt an,
    // statt zu ersetzen: Gruppen können größer sein als ein Block (die
    // Sammelgruppe ohne Warengruppe umfasst über tausend Artikel), und eine
    // stillschweigend abgeschnittene Gruppe wäre schlimmer als eine, die
    // ehrlich sagt, dass noch etwas fehlt.
    const loadGroupRows = useCallback(
        (groupKey, warengruppeValue, page = 1) => {
            setGroupRowsCache((prev) => ({
                ...prev,
                [groupKey]: {
                    rows: page > 1 ? (prev[groupKey]?.rows ?? []) : [],
                    total: prev[groupKey]?.total ?? null,
                    page,
                    loading: true,
                    loaded: false,
                    error: null,
                },
            }));

            const controller = new AbortController();
            groupRowAbortMapRef.current[groupKey]?.abort();
            groupRowAbortMapRef.current[groupKey] = controller;

            const params = {
                page,
                per_page: GROUP_CHUNK_SIZE,
                ...buildAdditionalParams(),
                ...buildFilterParams(),
                ...buildAdvancedSearchParams(),
                // Die Gruppe ohne Warengruppe wird über "__none__" angefordert:
                // ein leerer Wert bedeutet serverseitig "kein Filter" und würde
                // beim Aufklappen den kompletten Datensatz nachladen.
                [`filter[${groupingColumnId}]`]: warengruppeValue || "__none__",
            };
            if (searchCommitted) {
                params.search = searchCommitted;
            }
            if (sorting[0]) {
                params.sort = sorting[0].id;
                params.dir = sorting[0].desc ? "desc" : "asc";
            }

            axios
                .get(dataUrl, {params, signal: controller.signal})
                .then(({data}) => {
                    const fresh = Array.isArray(data?.rows) ? data.rows : [];
                    setGroupRowsCache((prev) => ({
                        ...prev,
                        [groupKey]: {
                            rows: page > 1 ? [...(prev[groupKey]?.rows ?? []), ...fresh] : fresh,
                            total: Number.isFinite(data?.total) ? data.total : null,
                            page,
                            loading: false,
                            loaded: true,
                            error: null,
                        },
                    }));
                })
                .catch((e) => {
                    if (axios.isCancel(e)) {
                        return;
                    }
                    setGroupRowsCache((prev) => ({
                        ...prev,
                        [groupKey]: {
                            rows: [],
                            loading: false,
                            loaded: false,
                            error: e.response?.data?.message || e.message || "Die Artikel dieser Warengruppe konnten nicht geladen werden.",
                        },
                    }));
                });
        },
        [buildAdditionalParams, buildFilterParams, buildAdvancedSearchParams, dataUrl, groupingColumnId, searchCommitted, sorting]
    );

    const handleToggleGroup = useCallback(
        (groupKey, warengruppeValue) => {
            // Der Nachlade-Aufruf gehört bewusst in den Event-Handler, nicht in
            // die setExpandedGroups-Updater-Funktion - Updater-Funktionen können
            // von React mehrfach aufgerufen werden und dürfen keine Seiteneffekte
            // (hier: Netzwerk-Request) auslösen.
            const willExpand = !expandedGroups[groupKey];
            setExpandedGroups((prev) => ({...prev, [groupKey]: willExpand}));
            if (willExpand) {
                const cached = groupRowsCache[groupKey];
                if (!cached || (!cached.loaded && !cached.loading)) {
                    loadGroupRows(groupKey, warengruppeValue);
                }
            }
        },
        [expandedGroups, groupRowsCache, loadGroupRows]
    );

    // Aufräumen aller offenen Anfragen beim Verlassen der Seite.
    useEffect(
        () => () => {
            dataAbortRef.current?.abort();
            groupsAbortRef.current?.abort();
            Object.values(groupRowAbortMapRef.current).forEach((controller) => controller.abort());
        },
        []
    );

    // Suche debounced übernehmen - siehe Vorgabe: Texteingaben ~350ms, Auswahllisten/Schalter sofort.
    const searchDebounceRef = useRef(null);
    useEffect(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        searchDebounceRef.current = setTimeout(() => {
            setSearchCommitted(searchInput.trim());
        }, 350);
        return () => {
            if (searchDebounceRef.current) {
                clearTimeout(searchDebounceRef.current);
            }
        };
    }, [searchInput]);

    // ----- Sichtbare Zeilen im Gruppierungsmodus zusammensetzen --------------
    // Tanstack braucht ein einzelnes `data`-Array, um Row-Objekte (samt
    // getValue/cell) zu liefern - deshalb werden die Zeilen aller AUFGEKLAPPTEN
    // UND geladenen Gruppen aneinandergehängt; die Start/End-Grenzen je Gruppe
    // merken sich, welcher Ausschnitt zu welcher Gruppe gehört.
    const groupedVisibleData = useMemo(() => {
        if (!grouping) {
            return {combined: [], boundaries: {}};
        }
        const combined = [];
        const boundaries = {};
        groupsList.forEach((group) => {
            const groupKey = group.warengruppe || "__none__";
            const entry = groupRowsCache[groupKey];
            if (expandedGroups[groupKey] && entry?.loaded) {
                const start = combined.length;
                combined.push(...entry.rows);
                boundaries[groupKey] = {start, end: combined.length};
            }
        });
        return {combined, boundaries};
    }, [grouping, groupsList, expandedGroups, groupRowsCache]);

    const tableData = grouping ? groupedVisibleData.combined : rows;

    // TanStack Table wählt ohne explizites filterFn automatisch "inNumberRange"
    // (Default ist filterFn: "auto"), sobald die Spaltenwerte Zahlen sind - diese
    // eingebaute Funktion erwartet den Filterwert als Array [min, max]. Der
    // eigene NumberRangeFilter setzt aber ein Objekt {min, max} (passend zum
    // Server-Filter filter[<spalte>][min]/[max]). inNumberRange.autoRemove liest
    // val[0]/val[1] - bei einem Objekt ist das immer undefined, weshalb TanStack
    // JEDEN gesetzten Zahlenbereichs-Filter sofort wieder aus columnFilters
    // entfernt, noch bevor buildFilterParams() ihn sieht (min wie max - bei min
    // fällt es nur seltener auf, weil kaum ein Artikel einen Bestand unter 1
    // hat). Ein eigenes, leeres filterFn (ohne .autoRemove) umgeht diese
    // automatische Erkennung - gefiltert wird wegen manualFiltering ohnehin
    // ausschliesslich serverseitig.
    const tableColumns = useMemo(
        () =>
            columns.map((col) =>
                col.meta?.filterVariant === "number" && !col.filterFn ? {...col, filterFn: () => true} : col
            ),
        [columns]
    );

    const table = useReactTable({
        data: tableData,
        columns: tableColumns,
        getRowId,
        state: {sorting, columnFilters, columnVisibility, columnOrder},
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        manualSorting: true,
        manualFiltering: true,
        manualPagination: true,
        rowCount: grouping ? groupsTotal : total,
        getCoreRowModel: getCoreRowModel(),
    });

    // Die Lagerübersicht kann ihre gemeinsamen Ansichten mit Spalten, Filtern
    // und Sortierung anwenden. Andere Einsätze bleiben bei der bisherigen,
    // rein lokalen Tabellensteuerung.
    useEffect(() => {
        onStateChange?.({
            columns: table.getVisibleLeafColumns().map((column) => column.id),
            columnOrder: table.getAllLeafColumns().map((column) => column.id),
            filters: columnFilters,
            sorting,
            additionalFilters: additionalFilterValues,
            search: searchCommitted,
            advancedSearch,
        });
    }, [columnVisibility, columnOrder, columnFilters, sorting, additionalFilterValues, searchCommitted, advancedSearch, onStateChange, table]);

    // Live-Ersatz einer einzelnen Zeile (z. B. nach "Jetzt aktualisieren" im
    // StatusTimestampControl) - trifft sowohl die aktuelle Seite als auch
    // bereits geladene Gruppen-Caches, in denen die Zeile stecken könnte.
    useImperativeHandle(
        ref,
        () => ({
            replaceRow(rowId, patch) {
                setRows((prev) => prev.map((r) => (getRowId(r) === rowId ? {...r, ...patch} : r)));
                setGroupRowsCache((prev) => {
                    let changed = false;
                    const next = {...prev};
                    Object.entries(prev).forEach(([key, entry]) => {
                        if (!entry?.rows?.some((r) => getRowId(r) === rowId)) {
                            return;
                        }
                        changed = true;
                        next[key] = {
                            ...entry,
                            rows: entry.rows.map((r) => (getRowId(r) === rowId ? {...r, ...patch} : r)),
                        };
                    });
                    return changed ? next : prev;
                });
            },
            applyState(state) {
                // Alte Ansichten enthalten nur `columns`. Deren Reihenfolge
                // bleibt der Fallback, bis eine Ansicht erstmals neu gesichert
                // wurde und dann `columnOrder` enthält.
                setColumnOrder(normaliseColumnOrder(state?.columnOrder || state?.columns || []));
                const visible = new Set(state?.columns || []);
                if (visible.size > 0) {
                    lockedColumnIds.forEach((id) => visible.add(id));
                    const nextVisibility = {};
                    columns.forEach((column) => {
                        nextVisibility[column.id] = visible.has(column.id);
                    });
                    setColumnVisibility(nextVisibility);
                }
                setColumnFilters(Array.isArray(state?.filters) ? state.filters : []);
                setSorting(Array.isArray(state?.sorting) ? state.sorting : initialSorting);
                setAdditionalFilterValues((current) => ({...current, ...(state?.additionalFilters || {})}));
                setSearchInput(state?.search || "");
                setSearchCommitted(state?.search || "");
                setAdvancedSearch(state?.advancedSearch || EMPTY_SEARCH);
                setGrouping(false);
            },
            getState() {
                return {
                    columns: table.getVisibleLeafColumns().map((column) => column.id),
                    columnOrder: table.getAllLeafColumns().map((column) => column.id),
                    filters: columnFilters,
                    sorting,
                    additionalFilters: additionalFilterValues,
                    search: searchCommitted,
                    advancedSearch,
                };
            },
        }),
        [getRowId, columns, columnFilters, columnOrder, sorting, additionalFilterValues, searchCommitted, advancedSearch, table, initialSorting, lockedColumnIds, normaliseColumnOrder]
    );

    const visibleColCount = table.getVisibleLeafColumns().length;
    const hideableColumns = table.getAllLeafColumns().filter((column) => !lockedColumnIds.includes(column.id));
    const hideableColumnsById = new Map(hideableColumns.map((column) => [column.id, column]));
    const configuredPickerColumnIds = new Set();
    const columnPickerSections = columnPickerGroups.length > 0
        ? [
            ...columnPickerGroups.map((group) => {
                const groupedColumns = (group.columnIds || [])
                    .map((id) => hideableColumnsById.get(id))
                    .filter(Boolean);
                groupedColumns.forEach((column) => configuredPickerColumnIds.add(column.id));

                return {label: group.label, columns: groupedColumns};
            }).filter((group) => group.columns.length > 0),
            {
                label: "Weitere",
                columns: hideableColumns.filter((column) => !configuredPickerColumnIds.has(column.id)),
            },
        ].filter((group) => group.columns.length > 0)
        : null;
    const groupingColumn = groupingColumnId ? columns.find((c) => c.id === groupingColumnId) : null;
    const groupingLabel = groupingColumn
        ? typeof groupingColumn.header === "string"
            ? groupingColumn.header
            : groupingColumnId
        : null;

    const filtersDeviated =
        grouping || hasCriteria(advancedSearch) || additionalFilters.some((f) => additionalFilterValues[f.key] !== f.defaultValue);

    const moveColumn = (columnId, targetId, afterTarget) => {
        setColumnOrder((current) => {
            const index = current.indexOf(columnId);
            const target = current.indexOf(targetId);
            if (index < 0 || target < 0 || index === target) return current;
            const next = current.filter((id) => id !== columnId);
            const targetIndex = next.indexOf(targetId);
            next.splice(targetIndex + (afterTarget ? 1 : 0), 0, columnId);
            return next;
        });
    };

    const onColumnHeaderDragStart = (event, columnId) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("application/x-datagrid-column", columnId);
    };

    const onColumnHeaderDrop = (event, targetId) => {
        const sourceId = event.dataTransfer.getData("application/x-datagrid-column");
        const bounds = event.currentTarget.getBoundingClientRect();
        const afterTarget = event.clientX > bounds.left + (bounds.width / 2);
        if (sourceId) moveColumn(sourceId, targetId, afterTarget);
    };

    // Der Dateiname steht im Content-Disposition-Header der Antwort. Er kommt
    // je nach Server in zwei Schreibweisen, deshalb beide Formen lesen.
    const filenameFromDisposition = (disposition, fallback) => {
        if (!disposition) {
            return fallback;
        }
        const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
        if (encoded) {
            try {
                return decodeURIComponent(encoded[1]);
            } catch {
                return fallback;
            }
        }
        const plain = /filename="?([^";]+)"?/i.exec(disposition);
        return plain ? plain[1].trim() : fallback;
    };

    // Der Export lief früher über window.location. Damit war das Ende des
    // Downloads für die Oberfläche nicht erkennbar - ein Ladehinweis hätte nur
    // geraten, wann er wieder verschwinden darf. Über einen normalen Abruf hat
    // der Vorgang ein messbares Ende, solange er läuft bleibt die Schaltfläche
    // gesperrt.
    const handleExport = async () => {
        if (!exportUrl || exporting) {
            return;
        }
        const params = new URLSearchParams();
        Object.entries(buildAdditionalParams()).forEach(([k, v]) => params.set(k, v));
        Object.entries(buildFilterParams()).forEach(([k, v]) => params.set(k, v));
        Object.entries(buildAdvancedSearchParams()).forEach(([k, v]) => params.set(k, v));
        if (searchCommitted) {
            params.set("search", searchCommitted);
        }
        if (sorting[0]) {
            params.set("sort", sorting[0].id);
            params.set("dir", sorting[0].desc ? "desc" : "asc");
        }
        params.set("columns", table.getVisibleLeafColumns().map((c) => c.id).join(","));

        setExporting(true);
        setExportError(null);

        let objectUrl = null;
        try {
            const response = await axios.get(`${exportUrl}?${params.toString()}`, {
                responseType: "blob",
            });

            const filename = filenameFromDisposition(
                response.headers?.["content-disposition"],
                "Export.xlsx",
            );

            objectUrl = URL.createObjectURL(response.data);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Der Verweis muss wieder freigegeben werden, sonst hält der Browser
            // die Datei bis zum Neuladen der Seite im Speicher. Aber NICHT sofort:
            // Der Download beginnt erst nach dem Klick, und ein sofort
            // widerrufener Verweis lässt ihn ins Leere laufen - dann kommt beim
            // Nutzer gar keine Datei an.
            const freizugeben = objectUrl;
            objectUrl = null;
            window.setTimeout(() => URL.revokeObjectURL(freizugeben), 60000);
        } catch {
            setExportError("Der Export konnte nicht erstellt werden. Bitte erneut versuchen.");
        } finally {
            // Nur aufräumen, wenn es gar nicht erst zum Download kam.
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
            setExporting(false);
        }
    };

    const renderRow = (row) => (
        <tr key={row.id}>
            {row.getVisibleCells().map((cell) => {
                const width = cell.column.columnDef.meta?.width;
                return (
                    <td
                        key={cell.id}
                        // Bei separaten Tabellenrahmen werden die horizontalen
                        // Bootstrap-Rahmen nicht mehr über die Tabellenzeile
                        // gemalt. Die Linie gehört deshalb explizit auf jede
                        // Datenzelle; vertikale Rahmen bleiben unverändert.
                        style={{
                            borderBottom: "var(--bs-border-width) solid var(--bs-table-border-color, var(--bs-border-color, #dee2e6))",
                            ...(width ? {width, maxWidth: width, ...(compactHeader ? {minWidth: width} : null)} : null),
                        }}
                    >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                );
            })}
        </tr>
    );

    const effectiveLastPage = Math.max(lastPage, 1);
    const allLoaded = rows.length >= total;

    // Nachladen beim Scrollen: Der Beobachter hängt am Fußbereich unter der
    // Tabelle. Er wird nur eingerichtet, solange gerade nicht geladen wird und
    // noch etwas fehlt - dadurch kann er nicht mehrfach denselben Block
    // anfordern, und er verschwindet von selbst, wenn alles geladen ist.
    useEffect(() => {
        if (grouping || loading || allLoaded) {
            return undefined;
        }
        const sentinel = loadMoreRef.current;
        const scrollBereich = scrollContainerRef.current;
        // Ohne den Scroll-Bereich würde der Beobachter auf das Fenster
        // zurückfallen und wieder am Seiten-Scroll hängen. Dann lieber gar
        // nicht beobachten als falsch.
        if (!sentinel || !scrollBereich || typeof IntersectionObserver === "undefined") {
            return undefined;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    setPage((p) => Math.min(p + 1, Math.max(effectiveLastPage, 1)));
                }
            },
            {
                // Gescrollt wird IN der Tabelle, nicht in der Seite - ohne
                // diesen Bezug würde der Beobachter am Seiten-Scroll hängen
                // und beim Scrollen in der Tabelle nie auslösen.
                root: scrollBereich,
                // Etwas vor dem sichtbaren Ende auslösen, damit der Nachschub
                // schon unterwegs ist, bevor der Nutzer am Listenende ankommt.
                rootMargin: "300px",
            },
        );
        observer.observe(sentinel);

        return () => observer.disconnect();
    }, [grouping, loading, allLoaded, effectiveLastPage]);

    return (
        <div>
            {error && (
                <Alert variant="danger" className="mb-2">
                    {error}
                </Alert>
            )}
            {groupsError && (
                <Alert variant="danger" className="mb-2">
                    {groupsError}
                </Alert>
            )}
            {exportError && (
                <Alert variant="danger" className="mb-2" dismissible onClose={() => setExportError(null)}>
                    {exportError}
                </Alert>
            )}

            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <Form.Control
                    size={compactHeader ? "sm" : undefined}
                    type="search"
                    placeholder="Suche über alle Spalten..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    style={{maxWidth: 320, ...(compactHeader ? {height: "31px"} : {})}}
                />

                {advancedSearchConfig && (
                    <div className="d-inline-flex align-items-center gap-1">
                        <Button
                            variant={hasCriteria(advancedSearch) ? "primary" : "outline-primary"}
                            size="sm"
                            className="d-inline-flex align-items-center gap-1"
                            onClick={() => setShowAdvancedSearch(true)}
                        >
                            <PiMagnifyingGlass/> Erweiterte Suche
                        </Button>
                        <HelpTooltip placement="bottom" size={0.75}>
                            Verknüpft mehrere Bedingungen und Bedingungsgruppen oder begrenzt die Tabelle exakt auf eine eingefügte Liste von Artikelnummern.
                        </HelpTooltip>
                    </div>
                )}

                <div className="position-relative d-inline-block">
                    <Dropdown autoClose="outside">
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                            {filtersDeviated ? <PiFunnelFill className="me-1" /> : <PiFunnel className="me-1" />}
                            Filter
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="p-2" style={{minWidth: 340}}>
                            {groupingLabel && (
                                <div className="d-flex align-items-center gap-1 mb-2">
                                    <Form.Check
                                        type="switch"
                                        id="datagrid-grouping-switch"
                                        label={`Nach ${groupingLabel} gruppieren`}
                                        checked={grouping}
                                        onChange={(e) => setGrouping(e.target.checked)}
                                    />
                                </div>
                            )}
                            {additionalFilters.map((f) => {
                                // "date" (einzelnes Datumsfeld, z. B. "von"/"bis") und
                                // "select" (Auswahlliste, z. B. Benutzer) ergänzen den
                                // ursprünglichen Boolean-Schalter um zwei weitere Filterarten
                                // im selben Popover - für Filter, die nicht in die
                                // Spaltenfilterzeile gehören.
                                if (f.type === "date") {
                                    return (
                                        <div key={f.key} className="d-flex align-items-center gap-2 mb-2">
                                            <span className="small text-nowrap" style={{minWidth: 32}}>
                                                {f.label}
                                            </span>
                                            <Form.Control
                                                type="date"
                                                size="sm"
                                                value={additionalFilterValues[f.key] || ""}
                                                onChange={(e) =>
                                                    setAdditionalFilterValues((prev) => ({...prev, [f.key]: e.target.value}))
                                                }
                                            />
                                            {f.help && (
                                                <HelpTooltip placement="right" size={0.75}>
                                                    {f.help}
                                                </HelpTooltip>
                                            )}
                                        </div>
                                    );
                                }

                                if (f.type === "select") {
                                    return (
                                        <div key={f.key} className="d-flex align-items-center gap-2 mb-2">
                                            <span className="small text-nowrap">{f.label}</span>
                                            <SelectList
                                                size="sm"
                                                value={additionalFilterValues[f.key] || ""}
                                                options={f.options || []}
                                                onChange={(v) =>
                                                    setAdditionalFilterValues((prev) => ({...prev, [f.key]: v}))
                                                }
                                                ariaLabel={f.label}
                                                style={{minWidth: 180}}
                                            />
                                            {f.help && (
                                                <HelpTooltip placement="right" size={0.75}>
                                                    {f.help}
                                                </HelpTooltip>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <div key={f.key} className="d-flex align-items-center gap-1 mb-1">
                                        <Form.Check
                                            type="switch"
                                            id={`datagrid-additional-${f.key}`}
                                            label={f.label}
                                            checked={!!additionalFilterValues[f.key]}
                                            onChange={(e) =>
                                                setAdditionalFilterValues((prev) => ({...prev, [f.key]: e.target.checked}))
                                            }
                                        />
                                        {f.help && (
                                            <HelpTooltip placement="right" size={0.75}>
                                                {f.help}
                                            </HelpTooltip>
                                        )}
                                    </div>
                                );
                            })}
                        </Dropdown.Menu>
                    </Dropdown>
                    {filtersDeviated && (
                        <span
                            className="position-absolute top-0 start-100 translate-middle p-1 bg-primary border border-light rounded-circle"
                            style={{width: 10, height: 10}}
                        >
                            <span className="visually-hidden">Filter aktiv</span>
                        </span>
                    )}
                </div>

                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm">
                        <PiEye className="me-1" />
                        Spalten
                    </Dropdown.Toggle>
                    <Dropdown.Menu className={columnPickerSections ? "p-0" : undefined}>
                        {columnPickerSections ? (
                            <div
                                className="p-3 overflow-auto"
                                style={{width: "min(760px, calc(100vw - 2rem))", maxHeight: "min(70vh, 640px)"}}
                            >
                                <div
                                    className="d-grid gap-3"
                                    style={{gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))"}}
                                >
                                    {columnPickerSections.map((group) => (
                                        <section key={group.label} aria-label={group.label}>
                                            <div className="small fw-semibold text-uppercase text-muted border-bottom pb-1 mb-2">
                                                {group.label}
                                            </div>
                                            {group.columns.map((column) => (
                                                <Form.Check
                                                    key={column.id}
                                                    type="checkbox"
                                                    id={`datagrid-col-vis-${column.id}`}
                                                    className="mb-2"
                                                    label={column.columnDef.meta?.pickerLabel || (typeof column.columnDef.header === "string" ? column.columnDef.header : column.id)}
                                                    checked={column.getIsVisible()}
                                                    onChange={column.getToggleVisibilityHandler()}
                                                />
                                            ))}
                                        </section>
                                    ))}
                                </div>
                            </div>
                        ) : hideableColumns.map((column) => (
                            <Dropdown.ItemText key={column.id} className="px-3 py-1 mb-0">
                                <Form.Check
                                    type="checkbox"
                                    id={`datagrid-col-vis-${column.id}`}
                                    label={column.columnDef.meta?.pickerLabel || (typeof column.columnDef.header === "string" ? column.columnDef.header : column.id)}
                                    checked={column.getIsVisible()}
                                    onChange={column.getToggleVisibilityHandler()}
                                />
                            </Dropdown.ItemText>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>

                {/* Ohne Export-Ziel gar nicht erst anbieten: eine dauerhaft
                    ausgegraute Schaltfläche mit Erklärtext verspricht etwas,
                    das es auf dieser Seite nicht gibt. */}
                {exportUrl && (
                    <div className="d-inline-flex align-items-center gap-1">
                        <Button
                            variant="success"
                            size="sm"
                            onClick={handleExport}
                            disabled={exporting}
                            className="d-inline-flex align-items-center gap-1"
                        >
                            {/* Spinner und Symbol sind gleich breit, damit die
                                Schaltfläche beim Wechsel nicht springt. */}
                            {exporting ? (
                                <Spinner
                                    animation="border"
                                    role="status"
                                    style={{width: "1em", height: "1em", borderWidth: "0.15em"}}
                                >
                                    <span className="visually-hidden">Export wird erstellt…</span>
                                </Spinner>
                            ) : (
                                <PiMicrosoftExcelLogo />
                            )}
                            Excel
                        </Button>
                        <HelpTooltip placement="bottom" size={0.75}>
                            Exportiert die Ansicht genau so, wie sie gerade gefiltert ist – ohne aktive Filter die
                            vollständige Liste.
                        </HelpTooltip>
                    </div>
                )}
                {toolbarActions}

                <span className="text-muted small ms-auto d-flex align-items-center gap-2">
                    {(loading || groupsLoading) && (
                        <Spinner animation="border" role="status" style={{width: "0.85rem", height: "0.85rem"}}>
                            <span className="visually-hidden">Wird aktualisiert…</span>
                        </Spinner>
                    )}
                    {grouping
                        ? `${groupsTotal} ${itemLabel} in ${groupsList.length} Warengruppen`
                        : `${total} ${itemLabel}`}
                    {resetAction}
                </span>
            </div>

            {advancedSearchConfig && (
                <AdvancedSearchModal
                    show={showAdvancedSearch}
                    onHide={() => setShowAdvancedSearch(false)}
                    value={advancedSearch}
                    onApply={setAdvancedSearch}
                    fields={advancedSearchConfig.fields || []}
                    initialSavedSearches={advancedSearchConfig.savedSearches || []}
                    saveUrl={advancedSearchConfig.saveUrl}
                />
            )}

            <div
                ref={scrollContainerRef}
                style={{
                    maxHeight: scrollMaxHeight ?? "calc(100vh - 210px)",
                    overflow: "auto",
                    // Eigener Stacking-Kontext, damit Tabellenzeilen niemals
                    // über eine sticky Kopf-/Filterzelle gezeichnet werden.
                    isolation: "isolate",
                    backgroundColor: "var(--bs-body-bg, #fff)",
                }}
            >
                <Table
                    bordered
                    hover
                    size="sm"
                    className="align-middle"
                    // Sticky Tabellenzellen und border-collapse führen in
                    // Chromium beim vertikalen Scrollen zu flackernden
                    // Zwischenräumen. Separate Borders halten die Kanten in
                    // derselben Stacking-Schicht wie die Deckflächen.
                    style={{borderCollapse: "separate", borderSpacing: 0, marginBottom: 0}}
                >
                    <thead>
                        <tr ref={headerRowRef}>
                            {table.getFlatHeaders().map((header) => {
                                const width = header.column.columnDef.meta?.width;
                                return (
                                    <th
                                        key={header.id}
                                        onClick={columnReorderMode ? undefined : header.column.getToggleSortingHandler()}
                                        draggable={columnReorderMode}
                                        onDragStart={(event) => onColumnHeaderDragStart(event, header.column.id)}
                                        onDragOver={(event) => columnReorderMode && event.preventDefault()}
                                        onDrop={(event) => {
                                            if (!columnReorderMode) return;
                                            event.preventDefault();
                                            onColumnHeaderDrop(event, header.column.id);
                                        }}
                                        className={compactHeader ? "small p-0 align-middle" : "text-nowrap"}
                                        style={{
                                            cursor: columnReorderMode ? "grab" : (header.column.getCanSort() ? "pointer" : "default"),
                                            ...stickyHeaderStyle,
                                            ...(width ? {width, maxWidth: width, ...(compactHeader ? {minWidth: width} : null)} : null),
                                        }}
                                    >
                                        {compactHeader ? (
                                            <div className="d-flex align-items-center justify-content-between gap-1 px-2 py-2" style={{minHeight: 52}}>
                                                <span className="d-inline-flex align-items-start gap-1 fw-semibold lh-sm" style={{minWidth: 0}}>
                                                    <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>
                                                    {header.column.columnDef.meta?.help && (
                                                        <span
                                                            className="flex-shrink-0"
                                                            onClick={(e) => e.stopPropagation()}
                                                            onPointerDown={(e) => e.stopPropagation()}
                                                        >
                                                            <HelpTooltip placement="bottom" size={0.75}>
                                                                {header.column.columnDef.meta.help}
                                                            </HelpTooltip>
                                                        </span>
                                                    )}
                                                </span>
                                                {header.column.getCanSort() && (
                                                    <span className="text-muted flex-shrink-0" aria-hidden="true">
                                                        {header.column.getIsSorted() === "asc" && <PiCaretUp />}
                                                        {header.column.getIsSorted() === "desc" && <PiCaretDown />}
                                                        {!header.column.getIsSorted() && <PiCaretUpDown />}
                                                    </span>
                                                )}
                                            </div>
                                        ) : <>
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                            {header.column.columnDef.meta?.help && (
                                                <span
                                                    className="ms-1"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onPointerDown={(e) => e.stopPropagation()}
                                                >
                                                    <HelpTooltip placement="bottom" size={0.75}>
                                                        {header.column.columnDef.meta.help}
                                                    </HelpTooltip>
                                                </span>
                                            )}
                                            {header.column.getCanSort() && (
                                                <span className="ms-1 text-muted">
                                                    {header.column.getIsSorted() === "asc" && <PiCaretUp />}
                                                    {header.column.getIsSorted() === "desc" && <PiCaretDown />}
                                                    {!header.column.getIsSorted() && <PiCaretUpDown />}
                                                </span>
                                            )}
                                        </>}
                                    </th>
                                );
                            })}
                        </tr>
                        <tr>
                            {table.getVisibleLeafColumns().map((column) => {
                                const width = column.columnDef.meta?.width;
                                return (
                                    <th
                                        key={column.id}
                                        className={compactHeader ? "small fw-normal p-1 align-middle" : "fw-normal"}
                                        style={{
                                            ...stickyFilterStyle,
                                            ...(width ? {width, maxWidth: width, ...(compactHeader ? {minWidth: width} : null)} : null),
                                        }}
                                    >
                                        {compactHeader ? <div className="d-flex align-items-center" style={{minHeight: 31}}><ColumnFilter column={column} /></div> : <ColumnFilter column={column} />}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody style={{opacity: loading || groupsLoading ? 0.6 : 1, transition: "opacity 120ms ease"}}>
                        {!grouping && rows.length === 0 && !loading && (
                            <tr>
                                <td colSpan={visibleColCount} className="text-center text-muted py-4">
                                    {emptyLabel}
                                </td>
                            </tr>
                        )}
                        {!grouping && table.getRowModel().rows.map((row) => renderRow(row))}

                        {grouping && groupsList.length === 0 && !groupsLoading && (
                            <tr>
                                <td colSpan={visibleColCount} className="text-center text-muted py-4">
                                    {emptyLabel}
                                </td>
                            </tr>
                        )}
                        {grouping &&
                            groupsList.map((group) => {
                                const groupKey = group.warengruppe || "__none__";
                                const label = group.warengruppe || "Ohne Warengruppe";
                                const isExpanded = !!expandedGroups[groupKey];
                                const cacheEntry = groupRowsCache[groupKey];
                                const boundary = groupedVisibleData.boundaries[groupKey];
                                const rowsOfGroup = boundary
                                    ? table.getRowModel().rows.slice(boundary.start, boundary.end)
                                    : [];

                                return (
                                    <React.Fragment key={groupKey}>
                                        <tr>
                                            <td colSpan={visibleColCount}>
                                                <span
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => handleToggleGroup(groupKey, group.warengruppe)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" || e.key === " ") {
                                                            e.preventDefault();
                                                            handleToggleGroup(groupKey, group.warengruppe);
                                                        }
                                                    }}
                                                    className="d-inline-flex align-items-center gap-1 fw-semibold"
                                                >
                                                    {isExpanded ? <PiCaretDown /> : <PiCaretRight />}
                                                    {label}
                                                    <span className="text-muted fw-normal">({group.count})</span>
                                                </span>
                                            </td>
                                        </tr>
                                        {isExpanded && cacheEntry?.loading && (
                                            <tr>
                                                <td colSpan={visibleColCount} className="text-center text-muted py-2">
                                                    <Spinner
                                                        animation="border"
                                                        role="status"
                                                        style={{width: "0.85rem", height: "0.85rem"}}
                                                        className="me-2"
                                                    />
                                                    Artikel werden geladen…
                                                </td>
                                            </tr>
                                        )}
                                        {isExpanded && cacheEntry?.error && (
                                            <tr>
                                                <td colSpan={visibleColCount}>
                                                    <Alert variant="danger" className="mb-0 py-1 small">
                                                        {cacheEntry.error}
                                                    </Alert>
                                                </td>
                                            </tr>
                                        )}
                                        {isExpanded && cacheEntry?.loaded && rowsOfGroup.map((row) => renderRow(row))}
                                        {isExpanded
                                            && cacheEntry?.loaded
                                            && !cacheEntry.loading
                                            && Number.isFinite(cacheEntry.total)
                                            && cacheEntry.rows.length < cacheEntry.total && (
                                            <tr>
                                                <td colSpan={visibleColCount} className="text-center py-2 small">
                                                    <span className="text-muted me-2">
                                                        {cacheEntry.rows.length} von {cacheEntry.total} Artikeln dieser
                                                        Warengruppe geladen
                                                    </span>
                                                    <Button
                                                        size="sm"
                                                        variant="outline-secondary"
                                                        onClick={() =>
                                                            loadGroupRows(groupKey, group.warengruppe, (cacheEntry.page || 1) + 1)
                                                        }
                                                    >
                                                        Weitere laden
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                    </tbody>
                </Table>
                {/* Kein seitenweises Blättern mehr: Der Fußbereich dient als
                    Auslöser fürs Nachladen und zeigt zugleich, wie weit die Liste
                    geladen ist. */}
                {!grouping && (
                    <div
                        ref={loadMoreRef}
                        className="d-flex flex-column align-items-center justify-content-center gap-1 mt-2 py-3"
                    >
                        {loading && (
                            <Spinner animation="border" role="status" style={{width: "1.1rem", height: "1.1rem"}}>
                                <span className="visually-hidden">Weitere {itemLabel} werden geladen…</span>
                            </Spinner>
                        )}
                        <span className="small text-muted">
                            {total === 0
                                ? `Keine ${itemLabel} gefunden`
                                : allLoaded
                                  ? `Alle ${total} ${itemLabelDative} geladen`
                                  : `${rows.length} von ${total} ${itemLabelDative} geladen`}
                        </span>
                    </div>
                )}
            </div>

            {grouping && (
                <div className="mt-2 small text-muted">
                    {groupsList.length} Warengruppen, {groupsTotal} Artikel
                </div>
            )}
        </div>
    );
});

export default DataGrid;

function ColumnFilter({column}) {
    const meta = column.columnDef.meta || {};
    const variant = meta.filterVariant;

    if (!variant || !column.getCanFilter()) {
        return null;
    }

    const headerLabel = typeof column.columnDef.header === "string" ? column.columnDef.header : column.id;

    if (variant === "select") {
        const filterValue = column.getFilterValue();
        return (
            <SelectList
                size="sm"
                value={filterValue ?? ""}
                options={meta.selectOptions || []}
                onChange={(v) => column.setFilterValue(v || undefined)}
                ariaLabel={`Filter: ${headerLabel}`}
            />
        );
    }

    if (variant === "number") {
        return <NumberRangeFilter column={column} />;
    }

    return <TextFilter column={column} />;
}

/**
 * Freitext-Spaltenfilter mit eigenem, entprellten Zwischenstand: getippt
 * wird sofort sichtbar, an den Server geht der Wert erst ~350ms nach der
 * letzten Eingabe - sonst würde jeder Tastendruck eine eigene Anfrage
 * auslösen.
 */
function TextFilter({column}) {
    const committed = column.getFilterValue();
    const [local, setLocal] = useState(committed ?? "");
    const debounceRef = useRef(null);

    useEffect(() => {
        setLocal(committed ?? "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [committed]);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        []
    );

    const handleChange = (e) => {
        const val = e.target.value;
        setLocal(val);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            column.setFilterValue(val || undefined);
        }, 350);
    };

    return <Form.Control size="sm" type="text" placeholder="Filter..." value={local} onChange={handleChange} />;
}

/**
 * Zahlen-Spaltenfilter (von/bis) - selbes Entprellungs-Prinzip wie {@link TextFilter}.
 */
function NumberRangeFilter({column}) {
    const committed = column.getFilterValue() || {};
    const [local, setLocal] = useState({min: committed.min ?? "", max: committed.max ?? ""});
    const debounceRef = useRef(null);

    useEffect(() => {
        setLocal({min: committed.min ?? "", max: committed.max ?? ""});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [committed.min, committed.max]);

    useEffect(
        () => () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        },
        []
    );

    const commit = (next) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            column.setFilterValue({
                min: next.min === "" ? undefined : Number(next.min),
                max: next.max === "" ? undefined : Number(next.max),
            });
        }, 350);
    };

    return (
        <div className="d-flex gap-1">
            <Form.Control
                size="sm"
                type="number"
                placeholder="min"
                value={local.min}
                onChange={(e) => {
                    const next = {...local, min: e.target.value};
                    setLocal(next);
                    commit(next);
                }}
            />
            <Form.Control
                size="sm"
                type="number"
                placeholder="max"
                value={local.max}
                onChange={(e) => {
                    const next = {...local, max: e.target.value};
                    setLocal(next);
                    commit(next);
                }}
            />
        </div>
    );
}
